import { Building } from '../constants';

// Position and bounds interfaces
export interface Position {
  x: number;
  y: number;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Collision layers define what types of entities exist in the game world
export enum CollisionLayer {
  TERRAIN = 'terrain', // Mountains, water, walls, trees, rocks
  BUILDINGS = 'buildings', // All placed structures (assemblers, miners, belts)
  PLAYER = 'player', // Player character
  ITEMS = 'items', // Belt items, dropped resources
  RESOURCES = 'resources', // Resource nodes (iron, copper)
}

// Entity types for collision system
export enum EntityType {
  PLAYER = 'player',
  BUILDING = 'building',
  TERRAIN = 'terrain',
  RESOURCE_NODE = 'resource_node',
  BELT_ITEM = 'belt_item',
}

// Base entity interface - everything that can collide
export interface CollisionEntity {
  id: string;
  type: EntityType;
  position: Position;
  bounds: Rectangle;
  collisionLayer: CollisionLayer;
  collisionMask: CollisionLayer[]; // What layers this entity collides with
  isSolid: boolean; // Can other entities pass through this?
  isStatic: boolean; // Does this entity move?
}

// Specific entity implementations
export interface PlayerEntity extends CollisionEntity {
  type: EntityType.PLAYER;
  facing: 'up' | 'down' | 'left' | 'right';
}

export interface BuildingEntity extends CollisionEntity {
  type: EntityType.BUILDING;
  building: Building;
  centerPosition: Position; // For multi-tile buildings
  occupiedPositions: Position[]; // All tiles this building occupies
}

export interface TerrainEntity extends CollisionEntity {
  type: EntityType.TERRAIN;
  terrainType: string; // Wall, tree, rock, water, mountain, etc.
}

export interface ResourceNodeEntity extends CollisionEntity {
  type: EntityType.RESOURCE_NODE;
  resourceType: 'iron' | 'copper';
}

// Union type for all entities
export type Entity =
  | PlayerEntity
  | BuildingEntity
  | TerrainEntity
  | ResourceNodeEntity;

// Collision result interface
export interface CollisionResult {
  canMove: boolean;
  canPlace: boolean;
  blockingEntities: Entity[];
  reason?: string;
  suggestedPositions?: Position[];
}

// Collision rule interface - defines what can collide with what
export interface CollisionRule {
  layer1: CollisionLayer;
  layer2: CollisionLayer;
  canOverlap: boolean;
  condition?: (entity1: Entity, entity2: Entity) => boolean;
  reason?: string;
}

// Validation result for building placement
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  blockingEntities: Entity[];
  suggestedPositions: Position[];
}

// Spatial query options
export interface QueryOptions {
  layers?: CollisionLayer[];
  entityTypes?: EntityType[];
  includeSolid?: boolean;
  includeStatic?: boolean;
}
